import { ChevronDown, ChevronRight, Pencil, Plus, RotateCcw, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { IconActionButton, TableIconActions } from '../ui/table-icon-actions'
import type { Department } from '../../types/department'

type DepartmentTreeRowsProps = {
  nodes: Department[]
  depth?: number
  canUpdate: boolean
  canDelete: boolean
  canRestore: boolean
  canCreate: boolean
  onEdit: (department: Department) => void
  onDelete: (department: Department) => void
  onRestore: (department: Department) => void
  onAddSubDepartment: (parent: Department) => void
}

function DepartmentTreeRow({
  node,
  depth,
  canUpdate,
  canDelete,
  canRestore,
  canCreate,
  onEdit,
  onDelete,
  onRestore,
  onAddSubDepartment,
}: {
  node: Department
  depth: number
} & Omit<DepartmentTreeRowsProps, 'nodes' | 'depth'>) {
  const hasChildren = (node.children?.length ?? 0) > 0
  const [expanded, setExpanded] = useState(true)

  return (
    <>
      <tr className="border-b border-parch-50 last:border-0">
        <td className="px-4 py-3">
          <div className="flex items-center gap-1" style={{ paddingLeft: `${depth * 1.25}rem` }}>
            {hasChildren ? (
              <button
                type="button"
                aria-label={expanded ? 'Collapse Sub-Departments' : 'Expand Sub-Departments'}
                className="rounded p-0.5 text-ink-400 hover:text-ink-700"
                onClick={() => setExpanded((value) => !value)}
              >
                {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </button>
            ) : (
              <span className="inline-block w-5" />
            )}
            <span className={node.deleted_at ? 'text-ink-400 line-through' : ''}>{node.name}</span>
            {depth > 0 && (
              <span className="ml-2 rounded bg-parch-100 px-1.5 py-0.5 text-xs text-ink-500">
                Level {depth + 1}
              </span>
            )}
          </div>
        </td>
        <td className="px-4 py-3">{node.is_active ? 'Yes' : 'No'}</td>
        <td className="px-4 py-3">
          {node.deleted_at ? (
            <span className="text-danger">Deleted</span>
          ) : (
            <span className="text-green-700">Active</span>
          )}
        </td>
        <td className="px-4 py-3">
          <TableIconActions>
            {canCreate && !node.deleted_at && (
              <IconActionButton
                icon={Plus}
                label="Add Sub-Department"
                variant="ghost"
                onClick={() => onAddSubDepartment(node)}
              />
            )}
            {canUpdate && !node.deleted_at && (
              <IconActionButton icon={Pencil} label="Edit" onClick={() => onEdit(node)} />
            )}
            {canDelete && !node.deleted_at && (
              <IconActionButton
                icon={Trash2}
                label="Delete"
                variant="destructive"
                onClick={() => onDelete(node)}
              />
            )}
            {canRestore && node.deleted_at && (
              <IconActionButton icon={RotateCcw} label="Restore" onClick={() => onRestore(node)} />
            )}
          </TableIconActions>
        </td>
      </tr>
      {hasChildren && expanded && (
        <DepartmentTreeRows
          nodes={node.children ?? []}
          depth={depth + 1}
          canUpdate={canUpdate}
          canDelete={canDelete}
          canRestore={canRestore}
          canCreate={canCreate}
          onEdit={onEdit}
          onDelete={onDelete}
          onRestore={onRestore}
          onAddSubDepartment={onAddSubDepartment}
        />
      )}
    </>
  )
}

export function DepartmentTreeRows({
  nodes,
  depth = 0,
  canUpdate,
  canDelete,
  canRestore,
  canCreate,
  onEdit,
  onDelete,
  onRestore,
  onAddSubDepartment,
}: DepartmentTreeRowsProps) {
  return (
    <>
      {nodes.map((node) => (
        <DepartmentTreeRow
          key={node.id}
          node={node}
          depth={depth}
          canUpdate={canUpdate}
          canDelete={canDelete}
          canRestore={canRestore}
          canCreate={canCreate}
          onEdit={onEdit}
          onDelete={onDelete}
          onRestore={onRestore}
          onAddSubDepartment={onAddSubDepartment}
        />
      ))}
    </>
  )
}
